import { Link } from 'react-router-dom'
import { useTierSummary } from '@/modules/billing/hooks'
import {
  aiMeterValue,
  voiceMeterExhaustedSuffix,
  voiceMeterValue,
} from '@/modules/billing/meter-labels'
import s from './TierWidget.module.css'

/* --- Diagonal striped fill sweep -------------------------------------------
   Pure-CSS port of the tenfour-site hero's "stop missing calls" wipe
   (tenfour-site/src/components/Hero/Hero.tsx). The button label's glyphs clip
   a wide (500%) diagonal gradient laid out [solid | slashes | hollow |
   slashes | solid]; a CSS @keyframes animation (TierWidget.module.css
   `tierSweep`) slides background-position-x 100% → 50% → 0% with springy
   cubic-bezier wipes and linear holds, so the label wipes solid → hollow →
   solid on a seamless 11s loop while the button is hovered. framer-motion is
   intentionally NOT used —
   the site dep stays out of the app bundle. */
const SWEEP_ANGLE = 110

// The label ink comes from the pill's theme (TierWidget.module.css
// `--sweep-ink`): white on the light theme's navy pill, navy on dark's light one.
const SWEEP_INK = 'var(--sweep-ink, #fffffd)'

function buildSlashes(start: number, end: number, count: number): string[] {
  const stops: string[] = []
  const step = (end - start) / (count * 2)
  for (let k = 0; k < count * 2; k += 1) {
    const a = (start + k * step).toFixed(2)
    const b = (start + (k + 1) * step).toFixed(2)
    const color = k % 2 === 0 ? SWEEP_INK : 'transparent'
    stops.push(`${color} ${a}%`, `${color} ${b}%`)
  }
  return stops
}

const FILL_GRADIENT = `linear-gradient(${SWEEP_ANGLE}deg, ${[
  `${SWEEP_INK} 0%`,
  `${SWEEP_INK} 30%`,
  ...buildSlashes(30, 34, 3),
  'transparent 34%',
  'transparent 66%',
  ...buildSlashes(66, 70, 3),
  `${SWEEP_INK} 70%`,
  `${SWEEP_INK} 100%`,
].join(', ')})`

/**
 * Sidebar plan widget (plans/subscription-tiers.md Phase 3): the free tier's
 * metered-usage bars and the animated Upgrade CTA. Sits between the nav scroll
 * area and the bottom user row, which is where the plan NAME lives — this
 * widget deliberately doesn't repeat it (plans/sidebar-identity-and-collapse.md).
 */
export function TierWidget({
  accountId,
  onNavigate,
}: {
  accountId: string
  onNavigate?: () => void
}) {
  const { data } = useTierSummary(accountId)
  if (!data) return null

  const showUpgrade = data.tier !== 'MAX'
  const usage = data.usage
  const percent = usage ? Math.min(100, Math.round(usage.percent)) : 100

  return (
    <div className={s.widget}>
      {/* Every meter reads the same way: a named header row (what's being
          measured · how much is used) directly ABOVE its bar, so a label can
          never be mistaken for the bar above it. */}
      <div className={s.meterHeader}>
        <span className={s.meterName}>AI usage</span>
        <span className={s.meterValue}>
          {usage
            ? usage.overCap
              ? 'Limit reached'
              : aiMeterValue(usage.monthUsd, usage.capUsd)
            : 'Unlimited'}
        </span>
      </div>
      <div
        className={[s.meter, usage?.overCap ? s.meterOver : ''].join(' ')}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={usage ? percent : undefined}
        aria-label={usage ? 'AI usage this month' : 'Unlimited usage'}
      >
        <div
          className={s.meterFill}
          style={{ width: usage ? `${Math.max(percent, 2)}%` : '100%' }}
        />
      </div>

      {/* Voice allowance meter (tier-config Phase 4) — rendered only when the
          tier has an allowance configured. SMS allowance logic is deliberately
          retained in the backend but is not a contractor-facing meter. */}
      {data.meters?.voice ? (
        <>
          <div className={s.meterHeader}>
            <span className={s.meterName}>Voice</span>
            <span className={s.meterValue}>
              {voiceMeterValue(data.meters.voice.usedMinutes, data.meters.voice.includedMinutes)}
              {voiceMeterExhaustedSuffix(
                data.meters.voice.exhausted,
                data.meters.voice.overageRatePerHourUsd,
              )}
            </span>
          </div>
          <div
            className={[s.meter, data.meters.voice.exhausted ? s.meterOver : ''].join(' ')}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={data.meters.voice.percent}
            aria-label="Voice minutes this month"
          >
            <div className={s.meterFill} style={{ width: `${Math.max(data.meters.voice.percent, 2)}%` }} />
          </div>
        </>
      ) : null}
      {showUpgrade ? (
        <Link to="/upgrade" className={s.upgradeBtn} onClick={onNavigate}>
          <span className={s.sweepWord}>
            <span
              className={s.sweepFill}
              aria-hidden="true"
              style={{ backgroundImage: FILL_GRADIENT }}
            >
              Upgrade
            </span>
            <span className={s.sweepOutline} aria-hidden="true">
              Upgrade
            </span>
            <span className={s.srOnly}>Upgrade</span>
          </span>
        </Link>
      ) : null}
    </div>
  )
}
